import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useReducer, useState } from "react";
// api
import {
    registerAPI, loginAPI, logoutAPI, forgotPasswordAPI, resetPasswordAPI,
    getUserAPI, modifyAccountAPI,
    resetPasswordNoLoginAPI,
} from "src/api";
// hooks
import { useWaitForDatalayer } from "src/hooks/use-wait-for-datalayer";
import { useWaitForFcWidget } from "src/hooks/use-wait-for-fcWidget";
// redux
import { persistor } from "src/redux/store";
// utils
import { saveParamToCookies } from "src/utils/url-query";
//
import { AuthContext } from "src/auth/context/jwt/auth-context";
import { isValidToken, setSession } from "src/auth/context/jwt/utils";

// ----------------------------------------------------------------------

// NOTE:
// We only build demo at basic level.
// Customer will need to do some extra handling yourself if you want to extend the logic and other features...

// ----------------------------------------------------------------------

const initialState = {
    loading: true,
    //
    user: null,
    company: null
};

const reducer = (state, action) => {
    if (action.type === "INITIAL") {
        const { authenticated, user } = action.payload || {};

        return {
            ...state,
            //
            loading: false,
            authenticated,
            ...( user && { user } )
        };
    }

    if (action.type === "LOGIN") {
        return {
            ...state,
            //
            authenticated: true
        };
    }

    if (action.type === "GET_USER") {
        const { user } = action.payload;

        return {
            ...state,
            user: {
                ...user
            }
        };
    }

    if (action.type === "MODIFY_USER") {
        const { user } = action.payload;

        return {
            ...state,
            user: {
                ...state.user,
                ...user
            }
        };
    }

    if (action.type === "LOGOUT") {
        return {
            ...state,
            user: null,
            authenticated: false
        };
    }

    return state;
};

// ----------------------------------------------------------------------

const STORAGE_KEY = "accessToken";

export function AuthProvider({ children }) {
    const [ state, dispatch ] = useReducer(reducer, initialState);

    // const { getData } = useVisitorData(
    //     { extendedResult: true },
    //     { immediate: true }
    // );

    const handelLogin = async (data) => {
        const accessToken = data?.jwt;
        const user = data?.jwt_payload;

        await localStorage.removeItem("email-recovery");

        if (accessToken) {
            await setSession(accessToken);
        }

        if (JSON.stringify(user)) {
            await localStorage.setItem("user", JSON.stringify(user));
        } else {
            await localStorage.removeItem("user");
        }

        await getUser();
    };

    const [ isDatalayerMounted, setIsDatalayerMounted ] = useState(false);
    const [ isFcWidgetMounted, setIsFcWidgetMounted ] = useState(false);

    useWaitForDatalayer(() => setIsDatalayerMounted(true));
    useWaitForFcWidget(() => setIsFcWidgetMounted(true));

    const register = useCallback(async (request) => {
        try {
            const res = await registerAPI(request);
            return res
        } catch (error) {
            return error;
        }
    }, []);

    const login = useCallback(async (request) => {
        const { status, data } = await loginAPI(request);

        if (status === 200) {
            await handelLogin(data?.data);
            // await trackLoginGETU(data?.data?.jwt_payload?.user_id, request.email);

            dispatch({ type: "LOGIN" });
        }
    }, []);

    const kicked = useCallback(async () => {
        await setSession(null);

        dispatch({ type: "LOGOUT" });

        localStorage.clear();

        persistor.purge().then(null);
    }, []);

    const logout = useCallback(async () => {
        const response = await logoutAPI();

        if (response.status === 200) {
            await kicked();
        }
    }, [ kicked ]);

    const forgotPassword = useCallback(async (request) => {
        const { status } = await forgotPasswordAPI(request);

        return status === 200;
    }, []);

    const resetPassword = useCallback(async (request) => {
        const { status } = await resetPasswordAPI(request);

        return status === 200;
    }, []);

    const resetPasswordNoLogin = useCallback(async (request) => {
        const { status } = await resetPasswordNoLoginAPI(request);

        return status === 200;
    }, []);

    const getUser = useCallback(async () => {
        const rawUser = localStorage.getItem("user");
        const user = rawUser ? JSON.parse(rawUser) : null;
        const { status: statusA, data: dataA } = await getUserAPI();

        dispatch({
            type: "GET_USER",
            payload: {
                user: {
                    ...user,
                    ...( statusA === 200 && ( dataA?.data ?? {} ) ),
                    // ...( statusB === 200 && ( { freshchat_restore_id: dataB?.data ?? "" } ) )
                }
            }
        });
    }, []);

    const modifyUser = useCallback((request) => modifyAccountAPI(request), []);

    const updateUser = useCallback((detail) => {
        dispatch({
            type: "MODIFY_USER",
            payload: {
                user: {
                    ...detail
                }
            }
        });
    }, []);

    // ----------------------------------------------------------------------

    const memoizedValue = useMemo(
        () => ( {
            method: "jwt",
            //
            loading: state.loading,
            authenticated: state.authenticated,
            user: state.user,
            //
            register,
            login,
            kicked,
            logout,
            forgotPassword,
            resetPassword,
            resetPasswordNoLogin,
            //
            getUser,
            modifyUser,
            updateUser
        } ),
        [ state.loading, state.authenticated, state.user, login, register, resetPasswordNoLogin, logout, kicked, forgotPassword, resetPassword, getUser, updateUser, modifyUser ]
    );

    // ----------------------------------------------------------------------

    useEffect(() => {
        const initialize = async () => {
            try {
                const accessToken = localStorage.getItem(STORAGE_KEY);

                if (accessToken && isValidToken(accessToken)) {
                    await setSession(accessToken);

                    await getUser();

                    dispatch({
                        type: "INITIAL",
                        payload: {
                            authenticated: true
                        }
                    });
                } else {
                    dispatch({
                        type: "INITIAL",
                        payload: {
                            user: null,
                            authenticated: false
                        }
                    });
                }
            } catch (e) {
                dispatch({
                    type: "INITIAL",
                    payload: {
                        user: null,
                        authenticated: false
                    }
                });
            }
        };

        initialize().then(null);
    }, []);

    useEffect(() => {
        if (isDatalayerMounted) {
            if (state.authenticated) {
                window.dataLayer.push({
                    event: "user_data_init",
                    user_data: {
                        first_name: state.user?.first_name ?? "",
                        last_name: state.user?.last_name ?? "",
                        email: state.user?.email ?? ""
                    },
                    freshchat_user: {
                        restoreId: state.user?.freshchat_restore_id ?? ""
                    }
                });
            }

            if (!state.authenticated) {
                window.dataLayer.push({
                    event: "user_data_clear",
                    user_data: {
                        first_name: "",
                        last_name: "",
                        email: ""
                    },
                    freshchat_user: {
                        restoreId: ""
                    }
                });
            }
        }
    }, [ isDatalayerMounted, state.user, state.authenticated ]);

    // ----------------------------------------------------------------------

    return <AuthContext.Provider value={ memoizedValue }>{ children }</AuthContext.Provider>;
}

AuthProvider.propTypes = {
    children: PropTypes.node
};
